import { Box, Checkbox } from '@material-ui/core';
import {
  Agent,
  AgentInLenderContext,
  AssetType,
  Channel,
  Lender,
  LenderMap,
  Product,
  Program,
} from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AllowedArticleTypesOptions } from '@/components/modules/AgentDetail/AgentDetails/components/Configuration/constants/AllowedArticleTypesOptions';
import { CredentialFieldLabel } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInLenderContextTable/components/AddOrEditAgentInLenderContextCredentialsDialog/components/CredentialFieldLabel';
import {
  getStoredArticleTypeLabelProps,
  getStoredCredentialFieldLabelProps,
} from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/constants/getStoredCredentialFieldLabelProps';
import { LenderOptions } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/constants/LenderOptions';
import { LoanProductOptions } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/constants/LoanProductOptions';
import { ProgramOptions } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/constants/ProgramOptions';
import { authenticationTypeMap } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/maps/authenticationTypeMap';
import {
  getCredentialFieldsConfig,
  getCredentialLenderNotice,
} from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/maps/credentialFieldConfigMap';
import { AuthenticationType } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/AuthenticationType';
import {
  CredentialFieldConfig,
  PlatformCredentialField,
} from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/CredentialFieldConfig';
import { Font } from '@/components/shared/components/Font';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { AutocompleteSelectFormField } from '@/components/shared/components/NewContextForms/AutocompleteSelectFormField';
import { PasswordFormField } from '@/components/shared/components/NewContextForms/components/PasswordFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

const CREDENTIAL_PLATFORM_FIELDS: Array<CredentialFieldConfig['platformField']> = [
  'merchantIdInLenderContext',
  'channelIdInLenderContext',
  'agentIdInLenderContext',
  'username',
  'password',
  'clientId',
  'clientSecret',
];

const FORM_FIELD_ROW_GAP = '20px';

const getCredentialFieldInputProps = (platformField: PlatformCredentialField, sensitive?: boolean) => {
  const isSecretField = sensitive || platformField === 'password' || platformField === 'clientSecret';

  return {
    autoComplete: isSecretField ? 'new-password' : 'off',
    'data-lpignore': 'true',
    'data-form-type': 'other',
  } as const;
};

interface AddOrEditAgentInLenderContextCredentialsFormProps {
  agent: Agent | undefined;
  lenders: Lender[];
  selectedAgentInLenderContext?: AgentInLenderContext;
  currentAgent?: Agent;
  currentChannel?: Channel;
  isEditable?: boolean;
}

export const AddOrEditAgentInLenderContextCredentialsForm = ({
  agent,
  lenders,
  currentAgent,
  currentChannel,
  selectedAgentInLenderContext,
}: AddOrEditAgentInLenderContextCredentialsFormProps) => {
  const { getTextIdTranslation } = useTranslation();

  const watch = useFormContext()?.watch;
  const reset = useFormContext()?.reset;
  const setValue = useFormContext()?.setValue;
  const getValues = useFormContext()?.getValues;

  const [allowArticleType, setAllowArticleType] = useState<boolean>(Boolean(selectedAgentInLenderContext?.articleType));
  const isMounted = useRef(false);
  const previousLenderProductProgramKey = useRef<string | undefined>(undefined);

  const selectedProduct: Product | undefined = watch ? watch('product') : undefined;
  const selectedProgram: Program | undefined = watch ? watch('program') : undefined;
  const selectedLenderId: string | undefined = watch ? watch('lenderId') : undefined;

  const lenderName = selectedLenderId ? LenderMap.fromLenderId(selectedLenderId) : undefined;
  const programOptions = selectedProduct ? ProgramOptions(selectedProduct, getTextIdTranslation) : [];

  const isLenderProductProgramSelected = Boolean(selectedLenderId && selectedProduct && selectedProgram);

  const fieldConfigs =
    lenderName && selectedProduct && selectedProgram
      ? getCredentialFieldsConfig(lenderName, selectedProduct, selectedProgram)
      : undefined;

  const lenderNotice =
    lenderName && selectedProduct && selectedProgram
      ? getCredentialLenderNotice(lenderName, selectedProduct, selectedProgram)
      : undefined;

  const lenderProductProgramKey = isLenderProductProgramSelected
    ? `${selectedLenderId}|${selectedProduct}|${selectedProgram}`
    : undefined;

  // Reset credential fields only when switching between two complete lender/product/program selections
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      previousLenderProductProgramKey.current = lenderProductProgramKey;
      return;
    }
    if (!lenderProductProgramKey) {
      return;
    }
    if (previousLenderProductProgramKey.current === lenderProductProgramKey) {
      return;
    }
    if (previousLenderProductProgramKey.current !== undefined) {
      const current = getValues ? getValues() : {};
      const resetValues: Record<string, null> = {};
      for (const field of CREDENTIAL_PLATFORM_FIELDS) {
        resetValues[field] = null;
      }
      reset?.({ ...current, ...resetValues });
    }
    previousLenderProductProgramKey.current = lenderProductProgramKey;
  }, [lenderProductProgramKey]);

  const setValueOptions = { shouldValidate: false, shouldDirty: true } as const;

  const hasChannelField = fieldConfigs
    ? fieldConfigs.some((f) => f.platformField === 'channelIdInLenderContext')
    : true;

  const hasAgentField = fieldConfigs ? fieldConfigs.some((f) => f.platformField === 'agentIdInLenderContext') : true;

  const hasChannelOrAgentField = (hasChannelField || hasAgentField) && !lenderNotice?.disableChannelAndAgentAutofill;

  const handleAutofillChannelAndAgent = () => {
    if (hasChannelField) {
      setValue?.('channelIdInLenderContext', currentChannel?.description ?? null, setValueOptions);
    }
    if (hasAgentField) {
      setValue?.('agentIdInLenderContext', currentAgent?.name ?? null, setValueOptions);
    }
  };

  const articleTypeOptions = AllowedArticleTypesOptions;

  const renderConfiguredFields = (configs: CredentialFieldConfig[]) =>
    configs.map((config) => {
      const fieldProps = {
        key: config.platformField,
        name: config.platformField,
        label: (
          <CredentialFieldLabel
            label={config.label}
            platformField={config.platformField}
            disableTranslation
            markAsOptional={!config.required}
            tooltip={config.tooltip}
          />
        ),
        placeholder: config.placeholder,
        inputProps: getCredentialFieldInputProps(config.platformField, config.sensitive),
        ...getStoredCredentialFieldLabelProps(config.platformField, selectedAgentInLenderContext),
      };

      return config.sensitive ? <PasswordFormField {...fieldProps} /> : <TextFormField {...fieldProps} />;
    });

  const renderFallbackFields = () => {
    // TODO: Remove once all lenders are in credentialFieldConfigMap
    const authenticationType = lenderName ? authenticationTypeMap.get(lenderName) : undefined;
    return (
      <>
        <TextFormField
          name={'merchantIdInLenderContext'}
          label={
            <CredentialFieldLabel label="MERCHANT_ID_IN_LENDER_CONTEXT" platformField="merchantIdInLenderContext" />
          }
          {...getStoredCredentialFieldLabelProps('merchantIdInLenderContext', selectedAgentInLenderContext)}
        />
        <TextFormField
          name={'channelIdInLenderContext'}
          label={
            <CredentialFieldLabel
              label="CHANNEL_ID_IN_LENDER_CONTEXT"
              platformField="channelIdInLenderContext"
              markAsOptional
            />
          }
          {...getStoredCredentialFieldLabelProps('channelIdInLenderContext', selectedAgentInLenderContext)}
        />
        <TextFormField
          name={'agentIdInLenderContext'}
          label={
            <CredentialFieldLabel
              label="AGENT_ID_IN_LENDER_CONTEXT"
              platformField="agentIdInLenderContext"
              markAsOptional
            />
          }
          {...getStoredCredentialFieldLabelProps('agentIdInLenderContext', selectedAgentInLenderContext)}
        />
        <TextFormField
          name={'market'}
          label={<CredentialFieldLabel label="MARKET" platformField="market" markAsOptional />}
          {...getStoredCredentialFieldLabelProps('market', selectedAgentInLenderContext)}
        />
        {authenticationType === AuthenticationType.PASSWORD && (
          <TextFormField
            name={'username'}
            label={<CredentialFieldLabel label="USERNAME" platformField="username" markAsOptional />}
            inputProps={getCredentialFieldInputProps('username')}
            {...getStoredCredentialFieldLabelProps('username', selectedAgentInLenderContext)}
          />
        )}
        {authenticationType === AuthenticationType.PASSWORD && (
          <PasswordFormField
            name={'password'}
            label={<CredentialFieldLabel label="PASSWORD" platformField="password" />}
            inputProps={getCredentialFieldInputProps('password', true)}
            {...getStoredCredentialFieldLabelProps('password', selectedAgentInLenderContext)}
          />
        )}
        {authenticationType === AuthenticationType.OAUTH_2 && (
          <>
            <TextFormField
              name={'clientId'}
              label={<CredentialFieldLabel label="CLIENT_ID_OAUTH_2" platformField="clientId" />}
              inputProps={getCredentialFieldInputProps('clientId')}
              {...getStoredCredentialFieldLabelProps('clientId', selectedAgentInLenderContext)}
            />
            <PasswordFormField
              name={'clientSecret'}
              label={<CredentialFieldLabel label="CLIENT_SECRET_OAUTH_2" platformField="clientSecret" />}
              inputProps={getCredentialFieldInputProps('clientSecret', true)}
              {...getStoredCredentialFieldLabelProps('clientSecret', selectedAgentInLenderContext)}
            />
          </>
        )}
      </>
    );
  };

  return (
    <Box pt={2}>
      <FormGridContainer fieldVariant={FieldVariant.form} fullWidth>
        <AutocompleteSelectFormField
          name={'lenderId'}
          label={'LENDER'}
          selectOptions={LenderOptions(lenders, getTextIdTranslation)}
        />
        <SelectFormField name={'product'} label={'PRODUCT'} selectOptions={LoanProductOptions} />
        <SelectFormField name={'program'} label={'PROGRAM'} selectOptions={programOptions} />

        <Box style={{ display: isLenderProductProgramSelected ? 'contents' : 'none' }}>
          {lenderNotice?.notice && (
            <Box style={{ gridColumn: '1 / -1' }}>
              <Font variant={'body2'} color={'textSecondary'}>
                {lenderNotice.notice}
              </Font>
            </Box>
          )}
          {fieldConfigs ? renderConfiguredFields(fieldConfigs) : renderFallbackFields()}
        </Box>
      </FormGridContainer>

      {isLenderProductProgramSelected && (
        <Box display="flex" flexDirection="column" style={{ paddingTop: FORM_FIELD_ROW_GAP, gap: '8px' }}>
          {hasChannelOrAgentField && (
            <Box display="flex" justifyContent="flex-start">
              <StandardButton type="button" variant="outlined" size="small" onClick={handleAutofillChannelAndAgent}>
                <Text tId="FILL_WITH_CHANNEL_AND_AGENT_DATA" />
              </StandardButton>
            </Box>
          )}

          {agent?.allowedAssetTypes.includes(AssetType.ORDER) && (
            <Box display={'flex'} flexDirection={'column'} style={{ gap: '12px' }}>
              <Box display={'flex'}>
                <Checkbox
                  checked={allowArticleType}
                  onChange={() => setAllowArticleType(!allowArticleType)}
                  style={{ marginLeft: -12 }}
                />
                <Box display="flex" flexDirection={'column'}>
                  <Font variant={'body2'} color={'textSecondary'}>
                    <Text tId={'SET_ARTICLE_TYPE'} />
                  </Font>
                  <Font variant={'caption'} color={'textSecondary'}>
                    <Text tId={'SET_ARTICLE_TYPE_EXPLANATION'} />
                  </Font>
                </Box>
              </Box>
              {allowArticleType && (
                <SelectFormField
                  name={'articleType'}
                  label={'ARTICLE'}
                  selectOptions={articleTypeOptions}
                  {...getStoredArticleTypeLabelProps(selectedAgentInLenderContext)}
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
